import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import WorkerLayout from '../../layouts/WorkerLayout';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import toast from 'react-hot-toast';

export default function WorkerMatches() {
  const [matches, setMatches] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);
  const [applied, setApplied] = useState(new Set());
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    api.get(`/worker/matches?page=${page}&limit=10`)
      .then((r) => { setMatches(r.data.data || []); setPagination(r.data.pagination || {}); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page]);

  async function applyToJob(requirementId) {
    setApplying(requirementId);
    try {
      await api.post(`/requirements/${requirementId}/apply`, {});
      setApplied((prev) => new Set([...prev, requirementId]));
      toast.success('Application submitted!');
    } catch {}
    finally { setApplying(null); }
  }

  return (
    <WorkerLayout>
      <div className="space-y-5 pb-20 sm:pb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Job Matches</h1>
          <p className="text-sm text-gray-500 mt-1">Opportunities matched to your skills and location</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin h-8 w-8 border-4 border-brand-500 border-t-transparent rounded-full" />
          </div>
        ) : matches.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <span className="text-4xl">🎯</span>
            <p className="text-gray-500 mt-3">No matches found yet</p>
            <p className="text-sm text-gray-400 mt-1">Complete your profile with skills to get better matches</p>
            <Link to="/worker/profile">
              <Button size="sm" className="mt-4">Complete Profile</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {matches.map((m) => {
              const isApplied = applied.has(m.requirement_id);
              return (
                <div key={m.requirement_id} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900 text-sm">{m.title}</h3>
                        <span className="bg-brand-50 text-brand-700 text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0">
                          {Math.round(m.match_score)}% match
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{m.company_name}</p>
                      <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-gray-500">
                        {m.location_text && <span>📍 {m.location_text}</span>}
                        {m.wage_min && <span>💰 ₹{m.wage_min}{m.wage_max ? `–${m.wage_max}` : ''}/day</span>}
                        {m.start_date && <span>📅 {new Date(m.start_date).toLocaleDateString('en-IN')}</span>}
                      </div>
                      {m.urgency && (
                        <div className="mt-2">
                          <Badge label={m.urgency} variant={m.urgency} />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    {isApplied ? (
                      <span className="text-sm text-green-600 font-medium py-1.5">✓ Applied</span>
                    ) : (
                      <Button size="sm" loading={applying === m.requirement_id}
                        onClick={() => applyToJob(m.requirement_id)}>
                        Apply Now
                      </Button>
                    )}
                    <Link to={`/worker/jobs/${m.requirement_id}`}>
                      <Button size="sm" variant="secondary">View Details</Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {pagination.totalPages > 1 && (
          <div className="flex justify-center gap-3">
            {pagination.hasPrev && (
              <Button size="sm" variant="secondary" onClick={() => setPage((p) => p - 1)}>← Prev</Button>
            )}
            <span className="text-sm text-gray-500 py-1.5">Page {pagination.page} of {pagination.totalPages}</span>
            {pagination.hasNext && (
              <Button size="sm" variant="secondary" onClick={() => setPage((p) => p + 1)}>Next →</Button>
            )}
          </div>
        )}
      </div>
    </WorkerLayout>
  );
}
